/**
 * Chat rules and the bounded message log. Pure: no DOM, no timers, no state.
 *
 * The server is the authority for every rule below (`server/src/room/`
 * validates the trimmed UTF-8 byte length, rejects control characters, assigns
 * the ID/time/identity, and keeps the 200-message buffer). This module mirrors
 * those bounds so the input can show a live counter and refuse an impossible
 * send *before* a round trip — an over-length paste that slips through is
 * still handled as an ordinary rejection.
 *
 * Messages are never rendered as markup. The text arrives untrusted, is kept
 * as a plain string, and components interpolate it with Svelte's default text
 * escaping (never `{@html}`), so a message that looks like `<script>` renders
 * as those characters.
 */

import type { ChatMessage } from '../generated';

/**
 * Server bound (`--chat-message-max-bytes`, default 2048): UTF-8 bytes of the
 * *trimmed* text. Counted in bytes, not characters, because that is what the
 * server measures — a 2000-character emoji message is over the limit.
 */
export const CHAT_MESSAGE_MAX_BYTES = 2048;

/**
 * Locally retained messages. Matches the server's join-history cap
 * (`--chat-history-limit`, default 200): keeping more would only display
 * history no reconnect could ever restore, while growing without bound.
 */
export const CHAT_LOG_LIMIT = 200;

const ENCODER = new TextEncoder();

export type ChatTextCheck =
  | { readonly ok: true; readonly text: string; readonly bytes: number }
  /** `message` is `null` for an empty box: nothing typed yet is not an error. */
  | { readonly ok: false; readonly bytes: number; readonly message: string | null };

/**
 * Validates one composed message against the server's rules and reports the
 * byte count either way, so the counter and the submit guard cannot disagree.
 */
export function checkChatText(raw: string): ChatTextCheck {
  const text = raw.trim();
  const bytes = ENCODER.encode(text).length;
  if (text === '') return { ok: false, bytes, message: null };
  if (bytes > CHAT_MESSAGE_MAX_BYTES) {
    return {
      ok: false,
      bytes,
      message: `Messages are at most ${CHAT_MESSAGE_MAX_BYTES} bytes; this one is ${bytes}.`,
    };
  }
  // Control characters (including newlines) would break the single-line
  // rendering and are refused by the server as well.
  if (/[\p{Cc}\p{Cf}]/u.test(text)) {
    return { ok: false, bytes, message: 'That message contains characters that are not allowed.' };
  }
  return { ok: true, text, bytes };
}

/**
 * Appends one broadcast message to the log, dropping the oldest beyond
 * `limit` and ignoring an ID already present.
 *
 * The duplicate check is what makes a reconnect safe: the fresh snapshot's
 * `recent_chat` overlaps whatever was already displayed, and a broadcast that
 * crosses a snapshot would otherwise appear twice.
 *
 * Returns the same array reference when nothing changed, so a duplicate does
 * not invalidate a rendered list.
 */
export function appendChatMessage(
  log: readonly ChatMessage[],
  message: ChatMessage,
  limit: number = CHAT_LOG_LIMIT,
): readonly ChatMessage[] {
  if (log.some((existing) => existing.id === message.id)) return log;
  const next = [...log, message];
  return next.length > limit ? next.slice(next.length - limit) : next;
}

/**
 * Merges a snapshot's `recent_chat` into the existing log. Reconnecting
 * replays history the client may already have, and may also have *lost*
 * messages while the socket was down, so the two lists are unioned by ID with
 * the server's order taken as the base.
 */
export function mergeChatHistory(
  log: readonly ChatMessage[],
  history: readonly ChatMessage[],
  limit: number = CHAT_LOG_LIMIT,
): readonly ChatMessage[] {
  const known = new Set(history.map((message) => message.id));
  // Anything displayed but not in the (bounded) history is older than the
  // server's window; keep it ahead of the history it precedes.
  const extra = log.filter((message) => !known.has(message.id));
  const merged = [...extra, ...history];
  return merged.length > limit ? merged.slice(merged.length - limit) : merged;
}

/**
 * Message age as short relative text.
 *
 * `sent_server_ms` is the server's *monotonic* clock (the same clock as
 * `RoomSnapshot.server_now_ms` and the playback anchors), not wall-clock time,
 * so it cannot be rendered as a time of day. Relative age against the
 * estimated server clock is the only meaning it carries. `null` (no estimate
 * yet) renders as nothing rather than a wrong number.
 */
export function formatChatAge(ageMs: number | null): string {
  if (ageMs === null) return '';
  if (ageMs < 45_000) return 'just now';
  const minutes = Math.round(ageMs / 60_000);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
}
